import type {
  User, Vessel, Fleet, Inspection, Questionnaire, QuestionnaireSection,
  Observation, CorrectiveAction, InspectionReport, Notification, Evidence,
  Question, VesselCertificate, SisterGroup, ColorCode, JobClassification
} from '../types';

// ========================
// USERS
// ========================
export const mockUsers: User[] = [
  {
    id: 'usr-001', name: 'James Mitchell', email: 'james.mitchell@risqnav.com',
    role: 'inspector', company: 'RISQ Navigator Ltd', avatar: '',
    phone: '+44 7700 123456', isActive: true, lastLogin: '2026-07-31T09:15:00Z', createdAt: '2024-01-15T00:00:00Z'
  },
  {
    id: 'usr-002', name: 'Sarah Chen', email: 'sarah.chen@maritimex.com',
    role: 'fleet_manager', company: 'MaritiMEX Shipping', avatar: '',
    phone: '+65 9123 4567', isActive: true, lastLogin: '2026-07-31T11:30:00Z', createdAt: '2024-03-10T00:00:00Z'
  },
  {
    id: 'usr-003', name: 'Captain Petrov', email: 'petrov@oceanfreight.com',
    role: 'vessel_crew', company: 'OceanFreight Corp', avatar: '',
    phone: '+7 916 123 4567', isActive: true, lastLogin: '2026-07-30T14:00:00Z', createdAt: '2024-06-20T00:00:00Z'
  },
  {
    id: 'usr-004', name: 'Admin User', email: 'admin@risqnav.com',
    role: 'admin', company: 'RISQ Navigator Ltd', avatar: '',
    isActive: true, lastLogin: '2026-08-01T08:00:00Z', createdAt: '2023-12-01T00:00:00Z'
  },
  {
    id: 'usr-005', name: 'Marco Rossi', email: 'marco.rossi@italship.it',
    role: 'inspector', company: 'ItalShip S.p.A', avatar: '',
    phone: '+39 333 456 7890', isActive: true, lastLogin: '2026-07-29T16:45:00Z', createdAt: '2024-08-05T00:00:00Z'
  },
];

export const currentUser = mockUsers[1]; // Fleet Manager

// ========================
// FLEETS
// ========================
export const mockFleets: Fleet[] = [
  { id: 'fl-001', name: 'Pacific Bulk Fleet', vesselCount: 12, complianceRate: 87.5, avgRightShipScore: 6.8, openFindings: 23 },
  { id: 'fl-002', name: 'Atlantic Tanker Fleet', vesselCount: 8, complianceRate: 92.1, avgRightShipScore: 7.4, openFindings: 11 },
  { id: 'fl-003', name: 'Global Container Fleet', vesselCount: 15, complianceRate: 78.3, avgRightShipScore: 5.9, openFindings: 45 },
];

// ========================
// VESSELS
// ========================
export const mockVessels: Vessel[] = [
  {
    id: 'vsl-001', name: 'MV Pacific Titan', imoNumber: 'IMO 9876543', mmsiNumber: '538007890',
    type: 'Bulk Carrier', flag: 'Panama', deadweight: 82000, grossTonnage: 42500, yearBuilt: 2019,
    classificationSociety: 'Lloyd\'s Register', manager: 'Pacific Maritime Ltd', superintendent: 'John Williams',
    lastRightShipScore: 7.2, status: 'active', fleetId: 'fl-001',
    totalInspections: 6, openFindings: 5, overdueActions: 2,
    lastInspectionDate: '2026-06-15', nextInspectionDate: '2026-09-15',
    location: { lat: 1.2900, lng: 103.8520, port: 'Singapore' },
    certificates: [
      { id: 'cert-001', name: 'Safety Equipment Certificate', number: 'SEC-2024-001', issuedBy: 'Lloyd\'s Register', issuedDate: '2024-03-15', expiryDate: '2029-03-15', status: 'valid' },
      { id: 'cert-002', name: 'Load Line Certificate', number: 'LLC-2024-002', issuedBy: 'Panama Maritime Authority', issuedDate: '2024-06-01', expiryDate: '2029-06-01', status: 'valid' },
      { id: 'cert-003', name: 'ISM Certificate', number: 'ISM-2023-015', issuedBy: 'Lloyd\'s Register', issuedDate: '2023-11-20', expiryDate: '2026-11-20', status: 'expiring_soon' },
    ],
    specifications: { lengthOverall: 229, beam: 32.2, depth: 20.1, draft: 14.5, engineType: 'MAN B&W 6S60ME-C', speed: 14.5 },
  },
  {
    id: 'vsl-002', name: 'MT Atlantic Pioneer', imoNumber: 'IMO 9876544', mmsiNumber: '636012345',
    type: 'Tanker', flag: 'Liberia', deadweight: 115000, grossTonnage: 58700, yearBuilt: 2017,
    classificationSociety: 'DNV', manager: 'Atlantic Shipping Corp', superintendent: 'Lars Andersen',
    lastRightShipScore: 8.1, status: 'active', fleetId: 'fl-002',
    totalInspections: 8, openFindings: 2, overdueActions: 0,
    lastInspectionDate: '2026-05-20', nextInspectionDate: '2026-11-20',
    location: { lat: 25.2850, lng: 55.3000, port: 'Dubai' },
    certificates: [
      { id: 'cert-004', name: 'International Oil Pollution Prevention', number: 'IOPP-2024-010', issuedBy: 'DNV', issuedDate: '2024-01-10', expiryDate: '2029-01-10', status: 'valid' },
      { id: 'cert-005', name: 'Safety Equipment Certificate', number: 'SEC-2024-011', issuedBy: 'DNV', issuedDate: '2024-02-15', expiryDate: '2029-02-15', status: 'valid' },
    ],
    specifications: { lengthOverall: 274, beam: 48, depth: 23.2, draft: 17.1, engineType: 'Wärtsilä 7RT-flex82T', speed: 15.2 },
  },
  {
    id: 'vsl-003', name: 'MV Nordic Star', imoNumber: 'IMO 9876545', mmsiNumber: '255805678',
    type: 'Container', flag: 'Hong Kong', deadweight: 65000, grossTonnage: 54200, yearBuilt: 2021,
    classificationSociety: 'Bureau Veritas', manager: 'Nordic Container Line', superintendent: 'Erik Johansson',
    lastRightShipScore: 6.5, status: 'under_inspection', fleetId: 'fl-003',
    totalInspections: 4, openFindings: 12, overdueActions: 3,
    lastInspectionDate: '2026-07-28', nextInspectionDate: '2026-08-01',
    location: { lat: 35.6762, lng: 139.6503, port: 'Tokyo' },
    certificates: [
      { id: 'cert-006', name: 'Safety Construction Certificate', number: 'SCC-2024-020', issuedBy: 'Bureau Veritas', issuedDate: '2024-05-01', expiryDate: '2029-05-01', status: 'valid' },
    ],
    specifications: { lengthOverall: 300, beam: 40, depth: 24.5, draft: 14.0, engineType: 'MAN B&W 9S90ME-C10', speed: 22.0 },
  },
  {
    id: 'vsl-004', name: 'MV Southern Grace', imoNumber: 'IMO 9876546', mmsiNumber: '219012345',
    type: 'General Cargo', flag: 'Marshall Islands', deadweight: 35000, grossTonnage: 19800, yearBuilt: 2015,
    classificationSociety: 'ClassNK', manager: 'Global Shipping Ltd', superintendent: 'Kenji Tanaka',
    lastRightShipScore: 5.8, status: 'active', fleetId: 'fl-001',
    totalInspections: 5, openFindings: 8, overdueActions: 4,
    lastInspectionDate: '2026-04-10', nextInspectionDate: '2026-10-10',
    location: { lat: -33.8688, lng: 151.2093, port: 'Sydney' },
    certificates: [
      { id: 'cert-007', name: 'ISM Certificate', number: 'ISM-2023-030', issuedBy: 'ClassNK', issuedDate: '2023-09-01', expiryDate: '2026-09-01', status: 'expiring_soon' },
      { id: 'cert-008', name: 'Safety Equipment Certificate', number: 'SEC-2024-031', issuedBy: 'ClassNK', issuedDate: '2024-01-15', expiryDate: '2029-01-15', status: 'valid' },
    ],
    specifications: { lengthOverall: 180, beam: 28, depth: 15.5, draft: 10.2, engineType: 'Wärtsilä 6L46F', speed: 13.8 },
  },
  {
    id: 'vsl-005', name: 'MT Sahara Voyager', imoNumber: 'IMO 9876547', mmsiNumber: '564012345',
    type: 'Tanker', flag: 'Singapore', deadweight: 48000, grossTonnage: 28900, yearBuilt: 2020,
    classificationSociety: 'Lloyd\'s Register', manager: 'Eastern Tanker Corp', superintendent: 'Raj Patel',
    lastRightShipScore: 7.8, status: 'active', fleetId: 'fl-002',
    totalInspections: 5, openFindings: 3, overdueActions: 1,
    lastInspectionDate: '2026-07-01', nextInspectionDate: '2027-01-01',
    location: { lat: 22.3193, lng: 114.1694, port: 'Hong Kong' },
    certificates: [
      { id: 'cert-009', name: 'International Tonnage Certificate', number: 'ITC-2024-045', issuedBy: 'Maritime and Port Authority of Singapore', issuedDate: '2024-04-20', expiryDate: '2029-04-20', status: 'valid' },
    ],
    specifications: { lengthOverall: 183, beam: 32.2, depth: 18.0, draft: 12.5, engineType: 'MAN B&W 6S50ME-C', speed: 14.0 },
  },
  {
    id: 'vsl-006', name: 'MV Nordic Compass', imoNumber: 'IMO 9876548', mmsiNumber: '255806789',
    type: 'Bulk Carrier', flag: 'Norway', deadweight: 75000, grossTonnage: 40100, yearBuilt: 2018,
    classificationSociety: 'DNV', manager: 'Nordic Bulk AS', superintendent: 'Ole Larsen',
    lastRightShipScore: 6.9, status: 'dry_dock', fleetId: 'fl-001',
    totalInspections: 7, openFindings: 6, overdueActions: 1,
    lastInspectionDate: '2026-03-20', nextInspectionDate: '2026-12-20',
    location: { lat: 58.9700, lng: 5.7331, port: 'Stavanger' },
    certificates: [
      { id: 'cert-010', name: 'Safety Construction Certificate', number: 'SCC-2024-055', issuedBy: 'DNV', issuedDate: '2024-06-10', expiryDate: '2029-06-10', status: 'valid' },
    ],
    specifications: { lengthOverall: 225, beam: 32.2, depth: 19.8, draft: 14.0, engineType: 'MAN B&W 5S60ME-C', speed: 14.2 },
  },
  {
    id: 'vsl-007', name: 'FV Starlight', imoNumber: 'IMO 9876549', mmsiNumber: '226012345',
    type: 'Ro-Ro', flag: 'Greece', deadweight: 28000, grossTonnage: 45600, yearBuilt: 2022,
    classificationSociety: 'Lloyd\'s Register', manager: 'Hellenic RoRo Ltd', superintendent: 'Nikos Papadopoulos',
    lastRightShipScore: 8.3, status: 'active', fleetId: 'fl-003',
    totalInspections: 3, openFindings: 1, overdueActions: 0,
    lastInspectionDate: '2026-06-25', nextInspectionDate: '2026-12-25',
    location: { lat: 37.9755, lng: 23.7349, port: 'Piraeus' },
    certificates: [
      { id: 'cert-011', name: 'Passenger Ship Safety Certificate', number: 'PSSC-2024-060', issuedBy: 'Lloyd\'s Register', issuedDate: '2024-02-01', expiryDate: '2029-02-01', status: 'valid' },
    ],
    specifications: { lengthOverall: 199, beam: 30, depth: 28.5, draft: 8.2, engineType: 'Wärtsilä 9L46F', speed: 20.5 },
  },
  {
    id: 'vsl-008', name: 'MV Phoenix Rising', imoNumber: 'IMO 9876550', mmsiNumber: '237012345',
    type: 'Bulk Carrier', flag: 'Malta', deadweight: 58000, grossTonnage: 32400, yearBuilt: 2016,
    classificationSociety: 'Bureau Veritas', manager: 'Mediterranean Shipping Co', superintendent: 'Andrea Bianchi',
    lastRightShipScore: 6.2, status: 'active', fleetId: 'fl-001',
    totalInspections: 9, openFindings: 9, overdueActions: 5,
    lastInspectionDate: '2026-05-05', nextInspectionDate: '2026-11-05',
    location: { lat: 36.8065, lng: 10.1815, port: 'Tunis' },
    certificates: [
      { id: 'cert-012', name: 'ISM Certificate', number: 'ISM-2023-075', issuedBy: 'Bureau Veritas', issuedDate: '2023-08-15', expiryDate: '2026-08-15', status: 'expiring_soon' },
    ],
    specifications: { lengthOverall: 190, beam: 32, depth: 18.0, draft: 12.5, engineType: 'MAN B&W 5S50ME-C', speed: 13.5 },
  },
];

// ========================
// SISTER GROUPS
// ========================
export const mockSisterGroups: SisterGroup[] = [
  { id: 'sg-001', name: 'Pacific Bulk Series', vesselIds: ['vsl-001', 'vsl-006'] },
  { id: 'sg-002', name: 'Atlantic Tanker Series', vesselIds: ['vsl-002', 'vsl-005'] },
];

// ========================
// QUESTIONNAIRE (RISQ 3.2)
// ========================
const risqSections: QuestionnaireSection[] = [
  {
    id: 'sec-001', title: 'Ship Particulars & Documentation', description: 'Vessel identification, classification, and documentation review', order: 1,
    questions: [
      { id: 'q-001', text: 'Is the vessel\'s IMO number clearly displayed and verified against documentation?', type: 'yes_no', sectionId: 'sec-001', isRequired: true, isRiskWeighted: false, riskWeight: 0, order: 1 },
      { id: 'q-002', text: 'Are all statutory certificates current and valid?', type: 'yes_no', sectionId: 'sec-001', isRequired: true, isRiskWeighted: true, riskWeight: 3, guidance: 'Check all certificates including Safety Construction, Safety Equipment, Load Line, ISM, ISPS, MARPOL', order: 2 },
      { id: 'q-003', text: 'Are classification society certificates up to date?', type: 'yes_no', sectionId: 'sec-001', isRequired: true, isRiskWeighted: true, riskWeight: 2, order: 3 },
      { id: 'q-004', text: 'Is the ISM Document of Compliance available and current?', type: 'yes_no', sectionId: 'sec-001', isRequired: true, isRiskWeighted: true, riskWeight: 3, order: 4 },
      { id: 'q-005', text: 'Last dry-dock date and next scheduled dry-dock', type: 'date', sectionId: 'sec-001', isRequired: true, isRiskWeighted: false, riskWeight: 0, order: 5 },
    ]
  },
  {
    id: 'sec-002', title: 'Safety Management System', description: 'ISM Code compliance and safety management', order: 2,
    questions: [
      { id: 'q-006', text: 'Is the Safety Management System manual available onboard?', type: 'yes_no', sectionId: 'sec-002', isRequired: true, isRiskWeighted: true, riskWeight: 3, order: 1 },
      { id: 'q-007', text: 'Are safety meetings conducted regularly and documented?', type: 'yes_no', sectionId: 'sec-002', isRequired: true, isRiskWeighted: true, riskWeight: 2, order: 2 },
      { id: 'q-008', text: 'Is there evidence of management review of safety performance?', type: 'yes_no', sectionId: 'sec-002', isRequired: true, isRiskWeighted: true, riskWeight: 2, order: 3 },
      { id: 'q-009', text: 'Rate the overall implementation of the Safety Management System', type: 'rating', sectionId: 'sec-002', isRequired: true, isRiskWeighted: true, riskWeight: 3, order: 4 },
      { id: 'q-010', text: 'Are emergency procedures clearly posted and known by crew?', type: 'yes_no', sectionId: 'sec-002', isRequired: true, isRiskWeighted: true, riskWeight: 3, order: 5 },
    ]
  },
  {
    id: 'sec-003', title: 'Fire Safety & Protection', description: 'Fire prevention, detection, and firefighting equipment', order: 3,
    questions: [
      { id: 'q-011', text: 'Are all fire detection and alarm systems operational?', type: 'yes_no', sectionId: 'sec-003', isRequired: true, isRiskWeighted: true, riskWeight: 3, order: 1 },
      { id: 'q-012', text: 'Are fire extinguishers properly maintained and within service dates?', type: 'yes_no', sectionId: 'sec-003', isRequired: true, isRiskWeighted: true, riskWeight: 2, order: 2 },
      { id: 'q-013', text: 'Are fire doors and dampers in good working condition?', type: 'yes_no', sectionId: 'sec-003', isRequired: true, isRiskWeighted: true, riskWeight: 2, order: 3 },
      { id: 'q-014', text: 'Is the fire main system tested and pressurized?', type: 'yes_no', sectionId: 'sec-003', isRequired: true, isRiskWeighted: true, riskWeight: 3, order: 4 },
      { id: 'q-015', text: 'Has a fire drill been conducted within the last month?', type: 'yes_no', sectionId: 'sec-003', isRequired: true, isRiskWeighted: true, riskWeight: 2, order: 5 },
      { id: 'q-016', text: 'Rate the overall fire safety condition', type: 'rating', sectionId: 'sec-003', isRequired: true, isRiskWeighted: true, riskWeight: 3, order: 6 },
    ]
  },
  {
    id: 'sec-004', title: 'Life-Saving Appliances', description: 'Lifeboats, life rafts, and related equipment', order: 4,
    questions: [
      { id: 'q-017', text: 'Are lifeboats in good condition and ready for immediate use?', type: 'yes_no', sectionId: 'sec-004', isRequired: true, isRiskWeighted: true, riskWeight: 3, order: 1 },
      { id: 'q-018', text: 'Are life rafts serviced and within valid service dates?', type: 'yes_no', sectionId: 'sec-004', isRequired: true, isRiskWeighted: true, riskWeight: 2, order: 2 },
      { id: 'q-019', text: 'Is rescue boat equipment complete and operational?', type: 'yes_no', sectionId: 'sec-004', isRequired: true, isRiskWeighted: true, riskWeight: 2, order: 3 },
      { id: 'q-020', text: 'Are lifejackets and immersion suits available in sufficient numbers?', type: 'yes_no', sectionId: 'sec-004', isRequired: true, isRiskWeighted: true, riskWeight: 3, order: 4 },
    ]
  },
  {
    id: 'sec-005', title: 'Navigation Safety', description: 'Bridge equipment, navigation, and communications', order: 5,
    questions: [
      { id: 'q-021', text: 'Is ECDIS properly maintained with up-to-date charts?', type: 'yes_no', sectionId: 'sec-005', isRequired: true, isRiskWeighted: true, riskWeight: 3, order: 1 },
      { id: 'q-022', text: 'Are radar and ARPA systems operational and tested?', type: 'yes_no', sectionId: 'sec-005', isRequired: true, isRiskWeighted: true, riskWeight: 2, order: 2 },
      { id: 'q-023', text: 'Is the GMDSS equipment maintained and tested?', type: 'yes_no', sectionId: 'sec-005', isRequired: true, isRiskWeighted: true, riskWeight: 2, order: 3 },
      { id: 'q-024', text: 'Are navigation publications and charts current?', type: 'yes_no', sectionId: 'sec-005', isRequired: true, isRiskWeighted: true, riskWeight: 2, order: 4 },
      { id: 'q-025', text: 'Rate the bridge team management effectiveness', type: 'rating', sectionId: 'sec-005', isRequired: true, isRiskWeighted: true, riskWeight: 3, order: 5 },
    ]
  },
  {
    id: 'sec-006', title: 'Machinery & Equipment', description: 'Main engine, auxiliaries, and mechanical systems', order: 6,
    questions: [
      { id: 'q-026', text: 'Is the main engine in good operational condition?', type: 'yes_no', sectionId: 'sec-006', isRequired: true, isRiskWeighted: true, riskWeight: 3, order: 1 },
      { id: 'q-027', text: 'Are auxiliary generators maintained and tested?', type: 'yes_no', sectionId: 'sec-006', isRequired: true, isRiskWeighted: true, riskWeight: 2, order: 2 },
      { id: 'q-028', text: 'Is the oil filtering equipment in compliance with MEPC.107(49)?', type: 'yes_no', sectionId: 'sec-006', isRequired: true, isRiskWeighted: true, riskWeight: 3, order: 3 },
      { id: 'q-029', text: 'Are maintenance records properly maintained (planned maintenance system)?', type: 'yes_no', sectionId: 'sec-006', isRequired: true, isRiskWeighted: true, riskWeight: 2, order: 4 },
      { id: 'q-030', text: 'Rate the overall machinery condition', type: 'rating', sectionId: 'sec-006', isRequired: true, isRiskWeighted: true, riskWeight: 3, order: 5 },
    ]
  },
  {
    id: 'sec-007', title: 'Environmental Compliance', description: 'MARPOL compliance and pollution prevention', order: 7,
    questions: [
      { id: 'q-031', text: 'Is the Oil Record Book properly maintained?', type: 'yes_no', sectionId: 'sec-007', isRequired: true, isRiskWeighted: true, riskWeight: 3, order: 1 },
      { id: 'q-032', text: 'Are sewage treatment plants operational and compliant?', type: 'yes_no', sectionId: 'sec-007', isRequired: true, isRiskWeighted: true, riskWeight: 2, order: 2 },
      { id: 'q-033', text: 'Is garbage management properly implemented?', type: 'yes_no', sectionId: 'sec-007', isRequired: true, isRiskWeighted: true, riskWeight: 2, order: 3 },
      { id: 'q-034', text: 'Are ballast water management procedures in place?', type: 'yes_no', sectionId: 'sec-007', isRequired: true, isRiskWeighted: true, riskWeight: 2, order: 4 },
    ]
  },
];

export const mockQuestionnaire: Questionnaire = {
  id: 'qn-001', name: 'RISQ 3.2 Standard Questionnaire', version: '3.2',
  description: 'RightShip Inspection Standard Questionnaire version 3.2 - Comprehensive vessel inspection checklist',
  sections: risqSections, isActive: true, createdAt: '2026-01-15', updatedAt: '2026-06-01', totalQuestions: 34,
};

// ========================
// INSPECTIONS
// ========================
export const mockInspections: Inspection[] = [
  {
    id: 'ins-001', vesselId: 'vsl-001', vesselName: 'MV Pacific Titan', questionnaireId: 'qn-001', questionnaireName: 'RISQ 3.2 Standard',
    inspectorId: 'usr-001', inspectorName: 'James Mitchell', status: 'approved', type: 'periodic',
    scheduledDate: '2026-06-10', startedDate: '2026-06-15T08:00:00Z', completedDate: '2026-06-17T16:30:00Z', approvedDate: '2026-06-20T10:00:00Z',
    progress: 100, totalQuestions: 34, answeredQuestions: 34, passCount: 29, failCount: 3, naCount: 2,
    observations: [], overallScore: 7.2, riskRating: 'medium', notes: 'Overall good condition. Minor issues with fire safety equipment and some documentation gaps.',
  },
  {
    id: 'ins-002', vesselId: 'vsl-003', vesselName: 'MV Nordic Star', questionnaireId: 'qn-001', questionnaireName: 'RISQ 3.2 Standard',
    inspectorId: 'usr-005', inspectorName: 'Marco Rossi', status: 'in_progress', type: 'initial',
    scheduledDate: '2026-07-28', startedDate: '2026-07-28T07:30:00Z',
    progress: 65, totalQuestions: 34, answeredQuestions: 22, passCount: 16, failCount: 4, naCount: 2,
    observations: [], notes: 'Inspection in progress. Some concerns noted in machinery section.',
  },
  {
    id: 'ins-003', vesselId: 'vsl-004', vesselName: 'MV Southern Grace', questionnaireId: 'qn-001', questionnaireName: 'RISQ 3.2 Standard',
    inspectorId: 'usr-001', inspectorName: 'James Mitchell', status: 'completed', type: 'periodic',
    scheduledDate: '2026-04-08', startedDate: '2026-04-10T08:00:00Z', completedDate: '2026-04-12T15:00:00Z',
    progress: 100, totalQuestions: 34, answeredQuestions: 34, passCount: 25, failCount: 6, naCount: 3,
    observations: [], overallScore: 5.8, riskRating: 'high', notes: 'Significant findings in fire safety and environmental compliance sections.',
  },
  {
    id: 'ins-004', vesselId: 'vsl-005', vesselName: 'MT Sahara Voyager', questionnaireId: 'qn-001', questionnaireName: 'RISQ 3.2 Standard',
    inspectorId: 'usr-005', inspectorName: 'Marco Rossi', status: 'approved', type: 'periodic',
    scheduledDate: '2026-06-28', startedDate: '2026-07-01T08:00:00Z', completedDate: '2026-07-03T17:00:00Z', approvedDate: '2026-07-05T09:00:00Z',
    progress: 100, totalQuestions: 34, answeredQuestions: 34, passCount: 31, failCount: 1, naCount: 2,
    observations: [], overallScore: 7.8, riskRating: 'low', notes: 'Excellent condition throughout. Minor observation on spare parts inventory.',
  },
  {
    id: 'ins-005', vesselId: 'vsl-002', vesselName: 'MT Atlantic Pioneer', questionnaireId: 'qn-001', questionnaireName: 'RISQ 3.2 Standard',
    inspectorId: 'usr-001', inspectorName: 'James Mitchell', status: 'under_review', type: 'periodic',
    scheduledDate: '2026-05-18', startedDate: '2026-05-20T08:00:00Z', completedDate: '2026-05-22T16:00:00Z',
    progress: 100, totalQuestions: 34, answeredQuestions: 34, passCount: 30, failCount: 2, naCount: 2,
    observations: [], overallScore: 8.1, riskRating: 'low', notes: 'Very well maintained vessel. Two minor observations on housekeeping.',
  },
  {
    id: 'ins-006', vesselId: 'vsl-008', vesselName: 'MV Phoenix Rising', questionnaireId: 'qn-001', questionnaireName: 'RISQ 3.2 Standard',
    inspectorId: 'usr-001', inspectorName: 'James Mitchell', status: 'draft', type: 'follow_up',
    scheduledDate: '2026-08-05', progress: 0, totalQuestions: 34, answeredQuestions: 0, passCount: 0, failCount: 0, naCount: 0,
    observations: [],
  },
];

// ========================
// OBSERVATIONS
// ========================
export const mockObservations: Observation[] = [
  {
    id: 'obs-001', inspectionId: 'ins-001', questionId: 'q-012', questionText: 'Are fire extinguishers properly maintained and within service dates?',
    sectionTitle: 'Fire Safety & Protection', title: 'Expired Fire Extinguisher in Engine Room',
    description: 'One dry powder fire extinguisher (E-107) found with service tag expired 3 months ago. Located in the engine room access corridor.',
    severity: 'major', riskLevel: 'high', photos: [], recommendation: 'Replace extinguisher immediately and update maintenance schedule.',
    inspectorId: 'usr-001', inspectorName: 'James Mitchell', createdAt: '2026-06-15T10:30:00Z',
  },
  {
    id: 'obs-002', inspectionId: 'ins-001', questionId: 'q-013', questionText: 'Are fire doors and dampers in good working condition?',
    sectionTitle: 'Fire Safety & Protection', title: 'Fire Door Seal Damaged',
    description: 'Fire door seal on ECR entrance fire door shows visible damage. Door self-closing mechanism partially functional.',
    severity: 'minor', riskLevel: 'medium', photos: [], recommendation: 'Replace fire door seal and verify self-closing mechanism.',
    inspectorId: 'usr-001', inspectorName: 'James Mitchell', createdAt: '2026-06-15T11:00:00Z',
  },
  {
    id: 'obs-003', inspectionId: 'ins-001', questionId: 'q-005', questionText: 'Last dry-dock date and next scheduled dry-dock',
    sectionTitle: 'Ship Particulars & Documentation', title: 'Overdue Hull Cleaning',
    description: 'Hull cleanliness below waterline showing significant marine growth. Vessel has not been dry-docked for 30 months.',
    severity: 'major', riskLevel: 'high', photos: [], recommendation: 'Schedule dry-docking within next 6 months for hull cleaning and underwater hull inspection.',
    inspectorId: 'usr-001', inspectorName: 'James Mitchell', createdAt: '2026-06-15T09:45:00Z',
  },
  {
    id: 'obs-004', inspectionId: 'ins-003', questionId: 'q-031', questionText: 'Is the Oil Record Book properly maintained?',
    sectionTitle: 'Environmental Compliance', title: 'Oil Record Book Entries Incomplete',
    description: 'Multiple entries in Oil Record Book Part I are missing or incomplete. Timestamps do not match actual operations.',
    severity: 'critical', riskLevel: 'high', photos: [], recommendation: 'Immediate remediation required. Crew must be retrained on proper Oil Record Book maintenance.',
    inspectorId: 'usr-001', inspectorName: 'James Mitchell', createdAt: '2026-04-11T14:00:00Z',
  },
  {
    id: 'obs-005', inspectionId: 'ins-003', questionId: 'q-011', questionText: 'Are all fire detection and alarm systems operational?',
    sectionTitle: 'Fire Safety & Protection', title: 'Smoke Detector Non-Functional',
    description: 'Smoke detector in cargo control room showing fault indicator. System log shows recurring fault for 2 weeks.',
    severity: 'critical', riskLevel: 'high', photos: [], recommendation: 'Replace smoke detector immediately. Investigate system for other faults.',
    inspectorId: 'usr-001', inspectorName: 'James Mitchell', createdAt: '2026-04-11T09:15:00Z',
  },
  {
    id: 'obs-006', inspectionId: 'ins-004', questionId: 'q-029', questionText: 'Are maintenance records properly maintained?',
    sectionTitle: 'Machinery & Equipment', title: 'Spare Parts Inventory Below Minimum',
    description: 'Critical spare parts inventory below minimum required levels. No evidence of recent reordering.',
    severity: 'minor', riskLevel: 'medium', photos: [], recommendation: 'Review and update spare parts inventory to meet minimum requirements.',
    inspectorId: 'usr-005', inspectorName: 'Marco Rossi', createdAt: '2026-07-02T11:30:00Z',
  },
];

// ========================
// CORRECTIVE ACTIONS
// ========================
export const mockCorrectiveActions: CorrectiveAction[] = [
  {
    id: 'capa-001', observationId: 'obs-001', vesselId: 'vsl-001', vesselName: 'MV Pacific Titan',
    fleetId: 'fl-001', inspectionId: 'ins-001', title: 'Replace Expired Fire Extinguisher E-107',
    description: 'Replace expired dry powder fire extinguisher in engine room access corridor. Verify maintenance schedule for all extinguishers.',
    owner: 'Chief Engineer', ownerId: 'usr-003', dueDate: '2026-07-15', priority: 'high', status: 'in_progress',
    rootCause: 'Maintenance scheduling oversight. No automated alerts for upcoming expirations.',
    closeoutComments: 'Extinguisher replaced on 2026-07-10. New maintenance schedule implemented with calendar alerts set 30 days before each expiry.',
    colorCode: 'yellow', jobClassification: 'fire_safety',
    photos: ['https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=200', 'https://images.unsplash.com/photo-1558618666-fcd25c85f82e?w=200'],
    closureEvidence: [], createdAt: '2026-06-17T10:00:00Z', updatedAt: '2026-07-10T14:30:00Z', isOverdue: false, daysRemaining: 14,
  },
  {
    id: 'capa-002', observationId: 'obs-002', vesselId: 'vsl-001', vesselName: 'MV Pacific Titan',
    fleetId: 'fl-001', inspectionId: 'ins-001', title: 'Repair Fire Door Seal - ECR Entrance',
    description: 'Replace damaged fire door seal and verify self-closing mechanism on ECR entrance fire door.',
    owner: 'Chief Officer', ownerId: 'usr-003', dueDate: '2026-07-30', priority: 'medium', status: 'open',
    colorCode: 'orange', jobClassification: 'fire_safety',
    closureEvidence: [], createdAt: '2026-06-17T10:00:00Z', updatedAt: '2026-06-17T10:00:00Z', isOverdue: false, daysRemaining: 29,
  },
  {
    id: 'capa-003', observationId: 'obs-003', vesselId: 'vsl-001', vesselName: 'MV Pacific Titan',
    fleetId: 'fl-001', inspectionId: 'ins-001', title: 'Schedule Dry-Docking for Hull Maintenance',
    description: 'Schedule dry-docking within 6 months for hull cleaning, underwater hull inspection, and propeller maintenance.',
    owner: 'Vessel Manager', ownerId: 'usr-002', dueDate: '2026-12-15', priority: 'high', status: 'assigned',
    colorCode: 'red', jobClassification: 'structural',
    photos: ['https://images.unsplash.com/photo-1545558014-8692077e9b5c?w=200'],
    closureEvidence: [], createdAt: '2026-06-17T10:00:00Z', updatedAt: '2026-06-20T10:00:00Z', isOverdue: false, daysRemaining: 167,
  },
  {
    id: 'capa-004', observationId: 'obs-004', vesselId: 'vsl-004', vesselName: 'MV Southern Grace',
    fleetId: 'fl-001', inspectionId: 'ins-003', title: 'Oil Record Book Remediation and Crew Training',
    description: 'Complete all missing Oil Record Book entries. Conduct training session on proper record-keeping per MARPOL requirements.',
    owner: 'Chief Engineer', ownerId: 'usr-003', dueDate: '2026-05-10', priority: 'critical', status: 'pending_review',
    rootCause: 'Insufficient crew training on MARPOL documentation requirements. Language barriers with some crew members.',
    actionTaken: 'Completed crew training session on 2026-05-05. All Oil Record Book entries have been corrected and verified.',
    closeoutComments: 'All entries corrected and verified. Training material updated with visual guides in 5 languages.',
    colorCode: 'green', jobClassification: 'environmental',
    photos: ['https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=200', 'https://images.unsplash.com/photo-1558618666-fcd25c85f82e?w=200', 'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=200'],
    closureEvidence: [], createdAt: '2026-04-12T15:00:00Z', updatedAt: '2026-05-08T09:00:00Z', isOverdue: false, daysRemaining: 0,
  },
  {
    id: 'capa-005', observationId: 'obs-005', vesselId: 'vsl-004', vesselName: 'MV Southern Grace',
    fleetId: 'fl-001', inspectionId: 'ins-003', title: 'Replace Non-Functional Smoke Detector',
    description: 'Replace faulty smoke detector in cargo control room. Investigate and test entire fire detection system.',
    owner: 'Chief Officer', ownerId: 'usr-003', dueDate: '2026-04-20', priority: 'critical', status: 'closed',
    rootCause: 'Smoke detector sensor degradation. Age-related component failure.',
    actionTaken: 'Smoke detector replaced on 2026-04-15. Full system test completed successfully. All detectors verified functional.',
    closeoutComments: 'Detector replaced. Full system scan performed - all 48 detectors functional. Added monthly test protocol.',
    colorCode: 'green', jobClassification: 'fire_safety',
    photos: ['https://images.unsplash.com/photo-1558618666-fcd25c85f82e?w=200'],
    closureEvidence: [], closedDate: '2026-04-18T11:00:00Z', verifiedBy: 'James Mitchell', verifiedDate: '2026-04-20T09:00:00Z',
    createdAt: '2026-04-12T15:00:00Z', updatedAt: '2026-04-20T09:00:00Z', isOverdue: false, daysRemaining: 0,
  },
  {
    id: 'capa-006', observationId: 'obs-006', vesselId: 'vsl-005', vesselName: 'MT Sahara Voyager',
    fleetId: 'fl-002', inspectionId: 'ins-004', title: 'Update Spare Parts Inventory',
    description: 'Review and update critical spare parts inventory to meet minimum required levels per Planned Maintenance System.',
    owner: 'Chief Engineer', ownerId: 'usr-003', dueDate: '2026-08-01', priority: 'medium', status: 'in_progress',
    closeoutComments: 'Inventory audit started. 60% of critical spares identified and reorder placed.',
    colorCode: 'yellow', jobClassification: 'machinery',
    closureEvidence: [], createdAt: '2026-07-03T17:00:00Z', updatedAt: '2026-07-15T10:00:00Z', isOverdue: false, daysRemaining: 0,
  },
  {
    id: 'capa-007', observationId: 'obs-001', vesselId: 'vsl-003', vesselName: 'MV Nordic Star',
    fleetId: 'fl-003', inspectionId: 'ins-002', title: 'ECDIS Software Update Required',
    description: 'ECDIS running outdated software version. Update to latest approved version with corrected ENC cells.',
    owner: 'Navigation Officer', ownerId: 'usr-003', dueDate: '2026-08-15', priority: 'high', status: 'in_progress',
    closeoutComments: 'Software downloaded. Scheduled installation during next port call.',
    colorCode: 'orange', jobClassification: 'navigation',
    closureEvidence: [], createdAt: '2026-07-28T10:00:00Z', updatedAt: '2026-07-30T14:00:00Z', isOverdue: false, daysRemaining: 13,
  },
  {
    id: 'capa-008', observationId: 'obs-003', vesselId: 'vsl-008', vesselName: 'MV Phoenix Rising',
    fleetId: 'fl-001', inspectionId: 'ins-006', title: 'Life Raft Service Overdue',
    description: 'Two life rafts have exceeded their 12-month service interval. Arrange emergency servicing.',
    owner: 'Chief Officer', ownerId: 'usr-003', dueDate: '2026-08-10', priority: 'critical', status: 'open',
    colorCode: 'red', jobClassification: 'life_saving',
    closureEvidence: [], createdAt: '2026-08-01T09:00:00Z', updatedAt: '2026-08-01T09:00:00Z', isOverdue: false, daysRemaining: 8,
  },
  {
    id: 'capa-009', observationId: 'obs-002', vesselId: 'vsl-006', vesselName: 'MV Nordic Compass',
    fleetId: 'fl-001', inspectionId: 'ins-001', title: 'Ballast Water Treatment System Calibration',
    description: 'BWT system showing irregular readings. Requires recalibration and filter inspection.',
    owner: 'Chief Engineer', ownerId: 'usr-003', dueDate: '2026-09-01', priority: 'medium', status: 'assigned',
    colorCode: 'grey_blue', jobClassification: 'environmental',
    closureEvidence: [], createdAt: '2026-03-25T10:00:00Z', updatedAt: '2026-04-01T08:00:00Z', isOverdue: false, daysRemaining: 30,
  },
  {
    id: 'capa-010', observationId: 'obs-004', vesselId: 'vsl-002', vesselName: 'MT Atlantic Pioneer',
    fleetId: 'fl-002', inspectionId: 'ins-005', title: 'Emergency Generator Test Log Missing',
    description: 'Emergency generator weekly test log entries missing for past 3 months. Retest and document.',
    owner: 'Chief Engineer', ownerId: 'usr-003', dueDate: '2026-08-05', priority: 'high', status: 'open',
    colorCode: 'orange', jobClassification: 'machinery',
    closureEvidence: [], createdAt: '2026-05-22T14:00:00Z', updatedAt: '2026-05-22T14:00:00Z', isOverdue: false, daysRemaining: 3,
  },
  {
    id: 'capa-011', observationId: 'obs-005', vesselId: 'vsl-002', vesselName: 'MT Atlantic Pioneer',
    fleetId: 'fl-002', inspectionId: 'ins-005', title: 'Housekeeping Deficiencies in Accommodation',
    description: 'Peeling paint in crew cabins, loose handrails on deck. General housekeeping requires attention.',
    owner: 'Chief Officer', ownerId: 'usr-003', dueDate: '2026-09-15', priority: 'low', status: 'assigned',
    colorCode: 'grey_blue', jobClassification: 'housekeeping',
    closureEvidence: [], createdAt: '2026-05-22T14:00:00Z', updatedAt: '2026-06-01T09:00:00Z', isOverdue: false, daysRemaining: 44,
  },
  {
    id: 'capa-012', observationId: 'obs-006', vesselId: 'vsl-007', vesselName: 'FV Starlight',
    fleetId: 'fl-003', inspectionId: 'ins-004', title: 'Update Safety Management Manual',
    description: 'ISM Safety Management Manual requires revision to incorporate latest company procedures.',
    owner: 'Master', ownerId: 'usr-003', dueDate: '2026-10-01', priority: 'medium', status: 'open',
    colorCode: 'yellow', jobClassification: 'documentation',
    closureEvidence: [], createdAt: '2026-06-28T10:00:00Z', updatedAt: '2026-06-28T10:00:00Z', isOverdue: false, daysRemaining: 60,
  },
];

// ========================
// REPORTS
// ========================
export const mockReports: InspectionReport[] = [
  {
    id: 'rpt-001', inspectionId: 'ins-001', vesselName: 'MV Pacific Titan',
    title: 'RISQ 3.2 Inspection Report - MV Pacific Titan', format: 'pdf',
    status: 'approved', generatedBy: 'James Mitchell', generatedAt: '2026-06-18T09:00:00Z',
    approvedBy: 'Sarah Chen', approvedAt: '2026-06-20T10:00:00Z',
    executiveSummary: 'The MV Pacific Titan was inspected on 15-17 June 2026 against the RISQ 3.2 Standard Questionnaire. The vessel achieved an overall score of 7.2/10 with 3 findings identified (2 major, 1 minor). The vessel demonstrates generally good maintenance standards with areas for improvement in fire safety equipment maintenance and hull condition management.',
    findings: mockObservations.filter(o => o.inspectionId === 'ins-001'),
    recommendations: ['Replace expired fire extinguisher immediately', 'Schedule hull cleaning and underwater inspection', 'Verify all fire door seals and self-closing mechanisms'],
    pageCount: 24, fileSize: 2450000,
  },
  {
    id: 'rpt-002', inspectionId: 'ins-003', vesselName: 'MV Southern Grace',
    title: 'RISQ 3.2 Inspection Report - MV Southern Grace', format: 'pdf',
    status: 'final', generatedBy: 'James Mitchell', generatedAt: '2026-04-13T10:00:00Z',
    executiveSummary: 'The MV Southern Grace was inspected on 10-12 April 2026. The vessel received a score of 5.8/10 with 6 findings (2 critical, 2 major, 2 minor). Immediate attention is required for environmental compliance and fire safety issues.',
    findings: mockObservations.filter(o => o.inspectionId === 'ins-003'),
    recommendations: ['Immediate remediation of Oil Record Book', 'Replace faulty smoke detector', 'Conduct comprehensive fire safety review', 'Schedule dry-docking'],
    pageCount: 31, fileSize: 3200000,
  },
  {
    id: 'rpt-003', inspectionId: 'ins-004', vesselName: 'MT Sahara Voyager',
    title: 'RISQ 3.2 Inspection Report - MT Sahara Voyager', format: 'pdf',
    status: 'approved', generatedBy: 'Marco Rossi', generatedAt: '2026-07-04T11:00:00Z',
    approvedBy: 'Sarah Chen', approvedAt: '2026-07-05T09:00:00Z',
    executiveSummary: 'The MT Sahara Voyager demonstrated excellent maintenance standards with an overall score of 7.8/10. Only 1 minor finding identified related to spare parts inventory management.',
    findings: mockObservations.filter(o => o.inspectionId === 'ins-004'),
    recommendations: ['Update spare parts inventory to minimum levels'],
    pageCount: 18, fileSize: 1800000,
  },
];

// ========================
// NOTIFICATIONS
// ========================
export const mockNotifications: Notification[] = [
  { id: 'not-001', type: 'alert', title: 'Critical Finding Alert', message: 'Critical finding on MV Southern Grace - Non-functional smoke detector in cargo control room', timestamp: '2026-04-11T09:20:00Z', isRead: false, source: 'ins-003' },
  { id: 'not-002', type: 'warning', title: 'Overdue Certificate', message: 'ISM Certificate for MV Phoenix Rising expires on 15 August 2026', timestamp: '2026-08-01T08:00:00Z', isRead: false, source: 'vsl-008' },
  { id: 'not-003', type: 'info', title: 'Inspection Scheduled', message: 'Follow-up inspection for MV Phoenix Rising scheduled for 5 August 2026', timestamp: '2026-07-28T14:00:00Z', isRead: false, source: 'ins-006' },
  { id: 'not-004', type: 'success', title: 'CAPA Verified', message: 'Corrective action for smoke detector replacement on MV Southern Grace has been verified', timestamp: '2026-04-20T09:05:00Z', isRead: true, source: 'capa-005' },
  { id: 'not-005', type: 'warning', title: 'Corrective Action Due Soon', message: 'Fire extinguisher replacement on MV Pacific Titan due in 14 days', timestamp: '2026-07-01T10:00:00Z', isRead: true, source: 'capa-001' },
  { id: 'not-006', type: 'alert', title: 'AI Risk Prediction', message: 'AI analysis predicts 78% probability of RightShip inspection failure for MV Phoenix Rising based on current trends', timestamp: '2026-07-30T06:00:00Z', isRead: false, source: 'analytics' },
  { id: 'not-007', type: 'info', title: 'Report Approved', message: 'Inspection report for MV Pacific Titan has been approved by Sarah Chen', timestamp: '2026-06-20T10:05:00Z', isRead: true, source: 'rpt-001' },
  { id: 'not-008', type: 'warning', title: 'High-Risk Finding', message: 'MV Southern Grace has 3 overdue corrective actions requiring immediate attention', timestamp: '2026-08-01T07:30:00Z', isRead: false, source: 'capa' },
];
